import { supabase } from '../lib/supabase';
import { logError } from '../lib/logError';
import { normalizePhone } from '../lib/phone';

/**
 * Social Seller e a venda que veio do número que ele coletou.
 *
 * Regra do dono (2026-09-09): o Social Seller recebe a taxa de venda do cargo dele (1,3%) sobre
 * TODA venda cujo lead teve o número coletado por ele, como uma linha própria
 * (`Venda (Social Seller)`), ao lado das linhas de closer, pré-venda e gestão. Social Seller não
 * faz pré-venda; a linha dele nunca substitui a do SDR.
 *
 * Medido antes da regra entrar: 3 vendas de agosto/2026 (R$ 73.000 de contrato) vieram de
 * números coletados pela Thamara e não geraram uma linha sequer para ela.
 *
 * A ligação vive em `ss_contatos_coletados` (`ghl_opportunity_id`, `telefone_canonico`,
 * `lead_id`). A busca é por prioridade: oportunidade do GHL, depois telefone, depois lead —
 * uma consulta por chave, só a próxima se a anterior não achar. Coletado apagado (`deleted_at`)
 * não conta. Cada consulta é só `select` + `eq`, de propósito: os harnesses de teste de venda
 * simulam um subconjunto pequeno do PostgREST (alguns só têm `select`/`eq`/`then`). O filtro
 * de apagado e a ordem ficam em JS; uma chave tem no máximo um punhado de linhas.
 */

const COLUNAS = 'id, data, social_seller, social_seller_id, telefone_canonico, lead_id, ghl_opportunity_id, deleted_at';

async function buscarPorChave(coluna, valor, contexto) {
    if (!valor) return null;
    const { data, error } = await supabase
        .from('ss_contatos_coletados')
        .select(COLUNAS)
        .eq(coluna, valor);
    if (error) {
        logError(error, { fn: 'buscarSocialSellerDoLead', coluna, valor, ...contexto });
        throw error;
    }
    const linhas = (Array.isArray(data) ? data : [])
        .filter((r) => r && r.social_seller_id && !r.deleted_at)
        .sort((a, b) => String(b.data ?? '').localeCompare(String(a.data ?? '')));
    return linhas[0] ?? null;
}

/**
 * Devolve o registro de coleta do lead desta venda, ou null.
 * Lança em erro de rede: a chamada acontece ANTES de qualquer escrita da venda, então um
 * throw aqui não deixa nada órfão, e comissão devida não some por falha silenciosa.
 */
export const buscarSocialSellerDoLead = async ({ ghlOpportunityId = null, telefone = null, leadId = null } = {}) => {
    const tel = normalizePhone(telefone) || null;
    const contexto = { ghlOpportunityId, telefone: tel, leadId };
    return (await buscarPorChave('ghl_opportunity_id', ghlOpportunityId, contexto))
        ?? (await buscarPorChave('telefone_canonico', tel, contexto))
        ?? (await buscarPorChave('lead_id', leadId, contexto))
        ?? null;
};

const canon = (nome) => String(nome ?? '').normalize('NFD').replace(/\p{M}/gu, '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR');

/**
 * Decide se a linha de Social Seller nasce. Pura.
 *
 * Não nasce quando não há coletado, ou quando o Social Seller é a mesma pessoa que já recebe
 * como vendedor ou pré-vendedor nesta venda — aí ele já está pago pelo papel maior, e a
 * segunda linha seria a mesma pessoa duas vezes pela mesma venda (o defeito de 2026-08-31).
 * Identidade por id quando os dois lados têm id; por nome canônico quando falta.
 */
export function resolveSocialSellerParticipation({ coletado, sellerId = null, sellerName = '', preSaleId = null, preSaleName = '' }) {
    if (!coletado || !coletado.social_seller_id) return { gerar: false, motivo: 'sem-coletado', nome: '', pessoaId: null, coletadoId: null };
    const mesmaPessoa = (id, nome) => (id && coletado.social_seller_id === id) || (!!canon(nome) && canon(nome) === canon(coletado.social_seller));
    const base = { nome: coletado.social_seller, pessoaId: coletado.social_seller_id, coletadoId: coletado.id };
    if (mesmaPessoa(sellerId, sellerName)) return { gerar: false, motivo: 'ss-e-o-vendedor', ...base };
    if (mesmaPessoa(preSaleId, preSaleName)) return { gerar: false, motivo: 'ss-e-o-pre-vendedor', ...base };
    return { gerar: true, motivo: 'numero-coletado', ...base };
}
